import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import SEOHead from "@/components/base/SEOHead";
import Breadcrumb, { getCategoryBreadcrumb } from "@/components/base/Breadcrumb";
import { categories, categorySlugMap } from "@/mocks/categories";
import { getArticlesByCategory } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.id === slug);
  const categoryName = categorySlugMap[slug] || slug;
  const articles = getArticlesByCategory(slug);

  const seoTitleMap: Record<string, string> = {
    saishizhibo: "赛事直播 - 全球顶级体育赛事在线直播",
    yundongkecheng: "运动课程 - 体系化专业运动课程在线学习",
    jianshenjihua: "健身计划 - AI智能个性化健身方案定制",
    tiyuzixun: "体育资讯 - 最新体育新闻动态深度分析",
  };

  return (
    <>
      <SEOHead
        title={seoTitleMap[slug] || `${categoryName} - 云体育`}
        description={category?.description || `${categoryName}板块，云体育为您提供专业的体育运动服务。`}
        keywords={`云体育,云体育${categoryName},在线${categoryName},体育运动`}
        canonicalUrl={`https://boannote.cn/category/${slug}`}
        ogImage={category?.image}
      />

      <main className="min-h-screen bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-6">
          <Breadcrumb items={getCategoryBreadcrumb(slug)} />
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-4 mb-10 flex-wrap">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
              {categoryName}
            </h1>
            <span className="text-sm text-foreground-500">
              {articles.length} 篇文章
            </span>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex gap-3 mb-10 overflow-x-auto pb-2 flex-nowrap">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.id}`}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                  slug === cat.id
                    ? "bg-primary-500 text-white"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200/70 border border-background-200/70"
                }`}
              >
                <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        <section className="max-w-7xl mx-auto px-4 md:px-6 pb-20">
          {articles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-300"
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3 flex-wrap">
                      <span>{article.publishDate}</span>
                      <span>·</span>
                      <span>{article.readTime}阅读</span>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors mb-2 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2">{article.excerpt}</p>
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      {article.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-inbox-line text-2xl text-foreground-400 w-6 h-6 flex items-center justify-center"></i>
              </div>
              <p className="text-foreground-600">该分类暂无文章</p>
            </div>
          )}
        </section>
      </main>
    </>
  );
}